import { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { GenreDropdown } from './GenreSelector';
import { 
  Link2, 
  Upload, 
  Music, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Crown,
  Image as ImageIcon,
  Info,
  FileAudio,
  X,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { 
  validateFile, 
  uploadFileWithRetry, 
  AUDIO_TYPES, 
  formatFileSize,
  createAcceptString,
  isIOSSafari
} from '../utils/uploadUtils';
import { forceUnlock, isLocked } from '../utils/scrollLock';
import MonetizeToggle from './MonetizeToggle';



// DSP Platform configs
const DSP_PLATFORMS = {
  spotify: { name: 'Spotify', icon: '🎧', color: 'bg-green-500', domain: 'open.spotify.com' },
  apple_music: { name: 'Apple Music', icon: '🍎', color: 'bg-pink-500', domain: 'music.apple.com' },
  soundcloud: { name: 'SoundCloud', icon: '☁️', color: 'bg-orange-500', domain: 'soundcloud.com' },
  audiomack: { name: 'Audiomack', icon: '🎵', color: 'bg-amber-500', domain: 'audiomack.com' }
};

export function UploadMethodSelector({ 
  uploadMethod, 
  onMethodChange, 
  tierConfig,
  uploadLimits 
}) {
  const canDirectUpload = tierConfig?.direct_upload_enabled || false;
  
  return (
    <div className="mb-6" data-testid="upload-method-selector">
      <Label className="text-sm mb-3 block">Upload Method</Label>
      
      <div className="grid grid-cols-2 gap-3">
        {/* DSP Link Option - Always available */}
        <button
          type="button"
          onClick={() => onMethodChange('dsp')}
          className={cn(
            "p-4 rounded-lg border-2 text-left transition-all",
            uploadMethod === 'dsp' 
              ? "border-green-500 bg-green-500/10" 
              : "border-border hover:border-border"
          )}
          data-testid="upload-method-dsp"
        >
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-5 h-5 text-green-500" />
            <span className="font-medium">Link from DSP</span>
            <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">FREE</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Link to Spotify, Apple Music, SoundCloud, or Audiomack
          </p>
        </button>

        {/* Direct Upload Option - Tier-gated */}
        <button
          type="button"
          onClick={() => canDirectUpload && onMethodChange('direct')}
          disabled={!canDirectUpload}
          className={cn(
            "p-4 rounded-lg border-2 text-left transition-all",
            uploadMethod === 'direct' 
              ? "border-purple-500 bg-purple-500/10" 
              : canDirectUpload 
                ? "border-border hover:border-border"
                : "border-border opacity-50 cursor-not-allowed"
          )}
          data-testid="upload-method-direct"
        >
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-5 h-5 text-purple-500" />
            <span className="font-medium">Upload File</span>
            <Badge className="ml-auto bg-purple-500/20 text-purple-400 text-xs">PRO+</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {canDirectUpload 
              ? `Upload MP3/WAV directly • ${uploadLimits?.direct_uploads_remaining || 0} remaining`
              : "Upgrade to Pro tier for direct uploads"}
          </p>
        </button>
      </div>

      {!canDirectUpload && uploadMethod === 'direct' && (
        <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Crown className="w-4 h-4 text-amber-500 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium text-amber-400">Upgrade Required</p>
              <p className="text-muted-foreground">
                Direct MP3/WAV uploads are available for Pro ($20/mo) and Elite ($35/mo) artists.
                DSP links are always free.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DSPLinkInput({ 
  dspUrl, 
  onUrlChange,
  platform,
  onPlatformDetected,
  embedUrl,
  onEmbedGenerated,
  onValidationResult
}) {
  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [resolvedData, setResolvedData] = useState(null);

  const validateUrl = async (url) => {
    if (!url) {
      setIsValid(null);
      setErrorMessage(null);
      setResolvedData(null);
      onPlatformDetected(null);
      onEmbedGenerated(null);
      if (onValidationResult) onValidationResult(null);
      return;
    }

    setValidating(true);
    setErrorMessage(null);
    
    try {
      // Call backend DSP resolve endpoint for strict validation
      const response = await fetch(`${API}/dsp/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      
      const data = await response.json();
      console.log('[DSPLinkInput] Validation result:', data);
      
      setResolvedData(data);
      setIsValid(data.is_valid);
      
      if (data.is_valid) {
        onPlatformDetected(data.platform);
        if (data.embed_url) {
          onEmbedGenerated(data.embed_url);
        }
        if (onValidationResult) {
          onValidationResult({
            isValid: true,
            platform: data.platform,
            dspId: data.dsp_id,
            canonicalUrl: data.dsp_url_canonical,
            embedUrl: data.embed_url,
            playableType: data.playable_type
          });
        }
      } else {
        setErrorMessage(data.error_message);
        onPlatformDetected(null);
        onEmbedGenerated(null);
        if (onValidationResult) {
          onValidationResult({
            isValid: false,
            error: data.error_message
          });
        }
      }
    } catch (err) {
      console.error('[DSPLinkInput] Validation error:', err);
      // Fallback to client-side detection if backend fails
      let detected = null;
      const urlLower = url.toLowerCase();
      
      if (urlLower.includes('spotify.com')) detected = 'spotify';
      else if (urlLower.includes('music.apple.com') || urlLower.includes('itunes.apple.com')) detected = 'apple_music';
      else if (urlLower.includes('soundcloud.com')) detected = 'soundcloud';
      else if (urlLower.includes('audiomack.com')) detected = 'audiomack';
      
      if (detected) {
        setIsValid(true);
        onPlatformDetected(detected);
        // Generate basic embed URL for Spotify
        if (detected === 'spotify' && url.includes('/track/')) {
          const trackId = url.split('/track/')[1]?.split('?')[0];
          if (trackId) {
            onEmbedGenerated(`https://open.spotify.com/embed/track/${trackId}`);
          }
        }
      } else {
        setIsValid(false);
        setErrorMessage('Could not validate URL. Please check the link.');
        onPlatformDetected(null);
      }
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      validateUrl(dspUrl);
    }, 500);
    return () => clearTimeout(debounce);
  }, [dspUrl]);

  return (
    <div className="space-y-3" data-testid="dsp-link-input">
      <div>
        <Label className="text-sm mb-1 block flex items-center gap-2">
          <Link2 className="w-4 h-4 text-green-500" />
          DSP Track Link *
        </Label>
        <div className="relative">
          <Input
            value={dspUrl}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="https://open.spotify.com/track/... or other DSP link"
            className={cn(
              "font-mono text-sm pr-10",
              isValid === true && "border-green-500/50",
              isValid === false && "border-red-500/50"
            )}
            data-testid="dsp-url-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {validating && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {!validating && isValid === true && <CheckCircle className="w-4 h-4 text-green-500" />}
            {!validating && isValid === false && <AlertCircle className="w-4 h-4 text-red-500" />}
          </div>
        </div>
      </div>

      {/* Platform detection badge */}
      {platform && (
        <div className="flex items-center gap-2">
          <span className="text-lg">{DSP_PLATFORMS[platform]?.icon}</span>
          <Badge className={cn("text-foreground text-xs", DSP_PLATFORMS[platform]?.color)}>
            {DSP_PLATFORMS[platform]?.name} Track Detected
          </Badge>
          {resolvedData?.playable_type === 'embed' && (
            <Badge variant="outline" className="text-xs text-green-400">
              <CheckCircle className="w-3 h-3 mr-1" />
              Embeddable
            </Badge>
          )}
          {resolvedData?.playable_type === 'external' && (
            <Badge variant="outline" className="text-xs text-amber-400">
              Opens Externally
            </Badge>
          )}
        </div>
      )}

      {/* Accepted platforms */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-muted-foreground">Accepted:</span>
        {Object.entries(DSP_PLATFORMS).map(([key, config]) => (
          <span key={key} className="text-xs text-muted-foreground flex items-center gap-1">
            {config.icon} {config.name}
          </span>
        ))}
      </div>

      {/* Error message with specific guidance */}
      {isValid === false && errorMessage && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-400">
              <p className="font-medium mb-1">Invalid Link</p>
              <p>{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {isValid === false && !errorMessage && (
        <p className="text-xs text-red-400">
          Please enter a valid track/song link from Spotify, Apple Music, SoundCloud, or Audiomack.
          Artist profiles, playlists, and albums are not accepted.
        </p>
      )}

      {/* Preview section - simplified to avoid Spotify embed errors on mobile */}
      {isValid && resolvedData?.embed_url && resolvedData?.playable_type === 'embed' && platform === 'spotify' && (
        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-400 text-sm">Track Verified & Ready</p>
              <p className="text-xs text-muted-foreground">
                Your Spotify track link has been validated and is ready to submit
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DirectUploadInput({
  fileUrl,
  onFileUploaded,
  audioFormat,
  tierConfig,
  getAuthHeaders
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const fileInputRef = useRef(null);
  
  const maxSizeMB = tierConfig?.max_file_size_mb || AUDIO_TYPES.maxSizeMB;
  const audioTypes = { ...AUDIO_TYPES, maxSizeMB };

  // Handle file selection from input
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    console.log('[TrackUpload] File selected:', file?.name);
    
    if (!file) {
      // User cancelled - don't show error
      return;
    }
    
    setUploadError(null);
    setShowSuccess(false);

    // Validate file using utility
    const validation = validateFile(file, audioTypes);
    if (!validation.valid) {
      setUploadError(validation.error);
      toast.error(validation.error);
      return;
    }

    console.log('[TrackUpload] File validated:', file.name, formatFileSize(file.size));
    setSelectedFile(file);
    setRetryCount(0);
    toast.success(`Selected: ${file.name}`);
  };

  // Remove selected file (for pre-upload state)
  const handleRemoveFile = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedFile(null);
    setUploadError(null);
    setUploadProgress(0);
    setShowSuccess(false);
    setRetryCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove uploaded file (for post-upload state) - also clears parent state
  const handleRemoveUploadedFile = () => {
    handleRemoveFile();
    onFileUploaded(null, null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    console.log('[TrackUpload] Starting upload for:', selectedFile.name);
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setShowSuccess(false);

    // Get auth headers
    const headers = { ...getAuthHeaders() };
    delete headers['Content-Type'];

    // Use the resilient upload utility with retry
    const result = await uploadFileWithRetry({
      file: selectedFile,
      url: `${API}/upload/audio`,
      headers,
      onProgress: setUploadProgress,
      maxRetries: 3,
      retryDelay: 1000
    });

    setUploading(false);

    if (result.success) {
      console.log('[TrackUpload] Upload success:', result.data);
      setShowSuccess(true);
      
      // CRITICAL: Call the parent's callback to update fileUrl state and duration
      onFileUploaded(result.data.file_url, result.data.audio_format, result.data.duration);
      toast.success('Audio file uploaded successfully!');
    } else {
      console.error('[TrackUpload] Upload failed:', result.error);
      setUploadError(result.error);
      setRetryCount(prev => prev + 1);
      toast.error(result.error);
      setUploadProgress(0);
    }
  };

  return (
    <div className="space-y-4" data-testid="direct-upload-input">
      {!fileUrl ? (
        <>
          {/* 
            CRITICAL: For iOS Safari compatibility, the file input MUST be:
            1. A real <input type="file"> element (not hidden, use opacity/position)
            2. Triggered by direct user gesture (no setTimeout, no programmatic click)
            3. The clickable area should be a <label> with htmlFor pointing to the input
          */}
          
          {/* Hidden file input - NOT overlaying the entire container */}
          <input
            ref={fileInputRef}
            type="file"
            accept={createAcceptString(audioTypes)}
            onChange={handleFileChange}
            id="audio-file-input-real"
            className="sr-only"
            aria-label="Select audio file"
          />
          
          {/* Show file selection UI OR file selected + upload button */}
          {selectedFile ? (
            /* File is selected - show file info and Upload/Remove buttons */
            <div className={cn(
              "border-2 rounded-lg p-6 text-center transition-all",
              showSuccess 
                ? "border-green-500/50 bg-green-500/5" 
                : uploadError 
                  ? "border-red-500/50 bg-red-500/5"
                  : "border-purple-500/50 bg-purple-500/5"
            )}>
              <div className="space-y-3">
                {showSuccess ? (
                  <div className="relative">
                    <CheckCircle className="w-10 h-10 mx-auto text-green-500 animate-bounce" />
                    <Sparkles className="w-4 h-4 absolute top-0 right-1/3 text-green-400 animate-pulse" />
                  </div>
                ) : (
                  <FileAudio className={cn(
                    "w-10 h-10 mx-auto",
                    uploadError ? "text-red-500" : "text-purple-500"
                  )} />
                )}
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                
                {uploading ? (
                  <div className="space-y-2">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {uploadProgress < 95 
                        ? `Uploading... ${uploadProgress}%` 
                        : uploadProgress < 100 
                          ? 'Processing...' 
                          : 'Complete!'}
                    </p>
                  </div>
                ) : showSuccess ? (
                  <p className="text-sm text-green-500 font-medium">Upload complete! ✓</p>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    {/* UPLOAD BUTTON - Separate from file input, type="button" to prevent form submit */}
                    <Button 
                      onClick={handleUpload} 
                      size="sm" 
                      type="button"
                      disabled={uploading}
                      data-testid="upload-file-btn"
                    >
                      {retryCount > 0 ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Upload
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                    {/* REMOVE BUTTON - Clear selection and allow re-select */}
                    <Button 
                      onClick={handleRemoveFile} 
                      variant="outline" 
                      size="sm" 
                      type="button"
                      disabled={uploading}
                      data-testid="remove-file-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* No file selected - show file picker trigger */
            <label 
              htmlFor="audio-file-input-real"
              className="block border-2 border-dashed border-border hover:border-border active:bg-muted/30 rounded-lg p-6 text-center transition-all cursor-pointer"
              data-testid="select-file-area"
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Tap to select audio file</p>
              <p className="text-xs text-muted-foreground mt-1">
                {audioTypes.displayName} • Max {maxSizeMB}MB
              </p>
              {isIOSSafari() && (
                <p className="text-xs text-amber-500 mt-2">
                  iOS: Tap the area above to browse files
                </p>
              )}
            </label>
          )}
          
          {/* Fallback browse button - only show when no file selected */}
          {!selectedFile && (
            <div className="flex justify-center">
              <label 
                htmlFor="audio-file-input-real"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer"
              >
                <Upload className="w-3 h-3 mr-2" />
                Browse Files
              </label>
            </div>
          )}
          
          {/* Error display with retry hint */}
          {uploadError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{uploadError}</span>
              </p>
              {retryCount > 0 && retryCount < 3 && (
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  Click "Retry Upload" to try again ({3 - retryCount} attempts remaining)
                </p>
              )}
            </div>
          )}

          {/* Info box */}
          <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
            <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Direct uploads are stored on our servers and streamed to listeners. 
              You must own or have rights to distribute this audio.
              {isIOSSafari() && ' Works with iPhone Files app.'}
            </p>
          </div>
        </>
      ) : (
        /* File uploaded state with success animation */
        <div className="border border-green-500/30 bg-green-500/5 rounded-lg p-4" data-testid="upload-success-state">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center relative">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-green-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-400">Upload Complete!</p>
              <p className="text-xs text-muted-foreground">
                {audioFormat?.toUpperCase()} file ready • Now fill in track details below
              </p>
            </div>
            <Button onClick={handleRemoveUploadedFile} variant="ghost" size="sm" title="Remove and select different file">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function LicenseAgreement({ accepted, onAcceptChange }) {
  return (
    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4" data-testid="license-agreement">
      <div className="flex items-start gap-3">
        <Checkbox 
          id="license-accept"
          checked={accepted}
          onCheckedChange={onAcceptChange}
          data-testid="license-checkbox"
        />
        <label htmlFor="license-accept" className="text-xs text-muted-foreground cursor-pointer">
          <strong className="text-purple-400">Direct Upload License Agreement:</strong> By uploading audio 
          files directly, you represent and warrant that you own or control all rights to the content. 
          You grant DMV Soundstage a non-exclusive, worldwide, royalty-free license to host, stream, 
          reproduce, and display the content for platform operation and promotion. You agree to indemnify 
          DMV Soundstage against any claims arising from your uploads.
        </label>
      </div>
    </div>
  );
}

export function UploadLimitsDisplay({ limits }) {
  if (!limits || !limits.has_artist_profile) return null;

  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="upload-limits-display">
      <div className="p-3 bg-muted/30 rounded-lg">
        <p className="text-xs text-muted-foreground">DSP Links</p>
        <p className="font-bold text-green-400">Unlimited</p>
        <p className="text-xs text-muted-foreground">{limits.dsp_uploads_used} used</p>
      </div>
      <div className="p-3 bg-muted/30 rounded-lg">
        <p className="text-xs text-muted-foreground">Direct Uploads</p>
        <p className="font-bold text-purple-400">
          {limits.direct_upload_enabled 
            ? (limits.direct_upload_limit === 'unlimited' ? '∞' : limits.direct_uploads_remaining)
            : 'Locked'}
        </p>
        {limits.direct_upload_enabled && (
          <p className="text-xs text-muted-foreground">
            {limits.direct_uploads_used} / {limits.direct_upload_limit === 'unlimited' ? '∞' : limits.direct_upload_limit} used
          </p>
        )}
      </div>
    </div>
  );
}

export default function TrackUploadForm({
  onTrackCreated,
  onSuccess,
  getAuthHeaders: propGetAuthHeaders,
  genres,
  artistProfile
}) {
  // Use auth context as fallback if getAuthHeaders not passed as prop
  const auth = useAuth();
  const getAuthHeaders = propGetAuthHeaders || auth.getAuthHeaders;
  
  const [uploadMethod, setUploadMethod] = useState('dsp');
  const [uploadLimits, setUploadLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  // DSP fields
  const [dspUrl, setDspUrl] = useState('');
  const [dspPlatform, setDspPlatform] = useState(null);
  const [embedUrl, setEmbedUrl] = useState('');

  // Direct upload fields
  const [fileUrl, setFileUrl] = useState('');
  const [audioFormat, setAudioFormat] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  const [licenseAccepted, setLicenseAccepted] = useState(false);
  const [coverArtUrl, setCoverArtUrl] = useState('');
  const [coverArtUploading, setCoverArtUploading] = useState(false);

  // Common fields
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Monetization state
  const [monetizeConfig, setMonetizeConfig] = useState({
    enabled: false, tip_jar: false, pay_to_unlock: false,
    unlock_price: '', subscription_only: false, boost_after: false,
  });
  const [lockConfig, setLockConfig] = useState(null);
  
  // Use a ref to track submission state (survives re-renders)
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    fetchUploadLimits();
  }, []);

  const fetchUploadLimits = async () => {
    try {
      const response = await fetch(`${API}/upload/my-limits`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setUploadLimits(await response.json());
      }
    } catch (error) {
      console.error('Error fetching upload limits:', error);
    } finally {
      setLoading(false);
    }
  };

  const tierConfig = uploadLimits ? {
    direct_upload_enabled: uploadLimits.direct_upload_enabled,
    max_file_size_mb: uploadLimits.max_file_size_mb
  } : null;

  // Handle cover art upload with content moderation
  const handleCoverArtUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Cover art must be under 5MB');
      return;
    }
    
    setCoverArtUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'cover_art');
      formData.append('moderate', 'true'); // Enable content moderation
      
      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        if (error.detail?.includes('nudity') || error.detail?.includes('explicit') || error.detail?.includes('inappropriate')) {
          toast.error('Image rejected: Contains inappropriate content. Please upload a different image.');
        } else {
          toast.error(error.detail || 'Failed to upload cover art');
        }
        return;
      }
      
      const result = await response.json();
      console.log('[TrackUploadForm] Cover art upload response:', result);
      
      // Handle relative URLs - prepend origin if needed
      let imageUrl = result.url;
      if (imageUrl && imageUrl.startsWith('/api/')) {
        // For same-origin production, the relative URL should work
        // But for preview, we need the full URL
        if (API && !API.startsWith('/')) {
          imageUrl = `${API.replace('/api', '')}${imageUrl}`;
        }
        console.log('[TrackUploadForm] Resolved cover art URL:', imageUrl);
      }
      setCoverArtUrl(imageUrl);
      toast.success('Cover art uploaded!');
    } catch (error) {
      console.error('Cover art upload error:', error);
      toast.error('Failed to upload cover art');
    } finally {
      setCoverArtUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    // Prevent any default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent double submission using both state and ref
    if (submitting || isSubmittingRef.current) {
      console.log('[TrackUploadForm] Already submitting, ignoring');
      return;
    }
    
    // Set submitting flag immediately using ref (faster than state)
    isSubmittingRef.current = true;
    
    // CRITICAL: Force unlock scroll before any async operation
    // This prevents scroll lock issues on mobile
    if (isLocked()) {
      console.log('[TrackUploadForm] Force unlocking scroll before submit');
      forceUnlock();
    }
    
    // Validate common fields
    if (!title) {
      toast.error('Track title is required');
      return;
    }
    if (!genre) {
      toast.error('Primary genre is required for chart eligibility');
      return;
    }
    if (!ownershipConfirmed) {
      toast.error('You must confirm ownership/rights to submit this track');
      return;
    }

    // Validate method-specific fields
    if (uploadMethod === 'dsp') {
      if (!dspUrl) {
        toast.error('DSP link is required');
        return;
      }
      if (!dspPlatform) {
        toast.error('Please enter a valid DSP link');
        return;
      }
    } else {
      if (!fileUrl) {
        toast.error('Please upload an audio file first');
        return;
      }
      if (!licenseAccepted) {
        toast.error('You must accept the license agreement for direct uploads');
        return;
      }
    }

    setSubmitting(true);

    try {
      const trackData = {
        title,
        primary_genre: genre,
        upload_method: uploadMethod,
        ownership_confirmed: ownershipConfirmed
      };

      // Always include cover art if uploaded (works for both DSP and direct)
      if (coverArtUrl) {
        trackData.cover_art_url = coverArtUrl;
      }

      if (uploadMethod === 'dsp') {
        trackData.dsp_url = dspUrl;
        trackData.dsp_platform = dspPlatform;
        trackData.embed_url = embedUrl;
        // Keep spotify_url for backwards compatibility
        if (dspPlatform === 'spotify') {
          trackData.spotify_url = dspUrl;
        }
      } else {
        trackData.file_url = fileUrl;
        trackData.audio_format = audioFormat;
        trackData.license_accepted = licenseAccepted;
        // Include duration if available from audio upload
        if (audioDuration > 0) {
          trackData.duration = audioDuration;
        }
      }

      console.log('[TrackUploadForm] Submitting track:', trackData.title, 'method:', uploadMethod, 'duration:', audioDuration);
      
      // Add monetization settings
      if (monetizeConfig.enabled) {
        trackData.monetization_settings = {
          tip_jar_enabled: monetizeConfig.tip_jar,
          subscription_only: monetizeConfig.subscription_only,
          boost_after_post: monetizeConfig.boost_after,
        };
        if (lockConfig && lockConfig.lock_type && lockConfig.lock_type !== 'public') {
          trackData.content_lock = lockConfig;
        }
      }
      
      // Create a fresh fetch request to avoid any body stream issues
      let response;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          // Create fresh request body each time
          const requestBody = JSON.stringify(trackData);
          
          response = await fetch(`${API}/tracks`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json', 
              ...getAuthHeaders() 
            },
            body: requestBody
          });
          
          // If we got a response, break out of retry loop
          break;
        } catch (fetchError) {
          retryCount++;
          console.warn(`[TrackUploadForm] Fetch attempt ${retryCount} failed:`, fetchError.message);
          
          if (retryCount > maxRetries) {
            throw fetchError;
          }
          
          // Wait a bit before retrying
          await new Promise(r => setTimeout(r, 500));
        }
      }

      // Read response carefully
      let result;
      try {
        const responseText = await response.text();
        result = responseText ? JSON.parse(responseText) : {};
        result = {
          ok: response.ok,
          data: response.ok ? result : null,
          error: response.ok ? null : (result.detail || result.message || 'Request failed')
        };
      } catch (parseError) {
        console.error('[TrackUploadForm] Response parse error:', parseError);
        result = {
          ok: response.ok,
          data: response.ok ? {} : null,
          error: response.ok ? null : 'Failed to parse response'
        };
      }

      if (result.ok && result.data) {
        toast.success(`Track added successfully! Eligible for: ${result.data.chart_eligible_for?.join(', ') || 'charts'}`);
        
        // Reset form
        setDspUrl('');
        setDspPlatform(null);
        setEmbedUrl('');
        setFileUrl('');
        setAudioFormat('');
        setAudioDuration(0);
        setLicenseAccepted(false);
        setCoverArtUrl('');
        setTitle('');
        setGenre('');
        setOwnershipConfirmed(false);
        
        // Refresh limits
        fetchUploadLimits();
        
        // Call success callback (supports both prop names)
        if (onSuccess) onSuccess(result.data);
        if (onTrackCreated) onTrackCreated(result.data);
      } else {
        console.error('[TrackUploadForm] Submit error:', result.error);
        toast.error(result.error || 'Failed to add track. Please try again.');
      }
    } catch (error) {
      console.error('[TrackUploadForm] Error creating track:', error);
      
      // Handle specific body stream errors
      if (error.message?.includes('Body is disturbed') || 
          error.message?.includes('locked') ||
          error.message?.includes('body stream')) {
        toast.error('Please try again. If the issue persists, refresh the page.');
      } else {
        toast.error('Failed to add track. Please check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const canSubmit = title && genre && ownershipConfirmed && (
    (uploadMethod === 'dsp' && dspUrl && dspPlatform) ||
    (uploadMethod === 'direct' && fileUrl && licenseAccepted)
  );

  return (
    <form 
      onSubmit={handleSubmit} 
      className="space-y-6" 
      data-testid="track-upload-form"
      style={{ touchAction: 'manipulation' }}
    >
      {/* Upload Limits Display */}
      <UploadLimitsDisplay limits={uploadLimits} />

      {/* Upload Method Selector */}
      <UploadMethodSelector
        uploadMethod={uploadMethod}
        onMethodChange={setUploadMethod}
        tierConfig={tierConfig}
        uploadLimits={uploadLimits}
      />

      {/* DSP Link Input */}
      {uploadMethod === 'dsp' && (
        <DSPLinkInput
          dspUrl={dspUrl}
          onUrlChange={setDspUrl}
          platform={dspPlatform}
          onPlatformDetected={setDspPlatform}
          embedUrl={embedUrl}
          onEmbedGenerated={setEmbedUrl}
        />
      )}

      {/* Direct Upload Input */}
      {uploadMethod === 'direct' && tierConfig?.direct_upload_enabled && (
        <>
          <DirectUploadInput
            fileUrl={fileUrl}
            onFileUploaded={(url, format, duration) => {
              setFileUrl(url);
              setAudioFormat(format);
              if (duration) setAudioDuration(duration);
            }}
            audioFormat={audioFormat}
            tierConfig={tierConfig}
            getAuthHeaders={getAuthHeaders}
          />
          
          <LicenseAgreement
            accepted={licenseAccepted}
            onAcceptChange={setLicenseAccepted}
          />
        </>
      )}

      {/* Cover Art Upload - Available for ALL upload methods */}
      <div className="space-y-2" data-testid="cover-art-upload">
        <Label className="text-sm mb-1 block flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-purple-500" />
          Cover Art {uploadMethod === 'dsp' ? '(Optional)' : '(Recommended)'}
        </Label>
        <div className="flex items-center gap-4">
          {coverArtUrl ? (
            <div className="relative">
              <img 
                src={coverArtUrl.startsWith('http') ? coverArtUrl : 
                     (coverArtUrl.startsWith('/api/') ? coverArtUrl : 
                      (API ? `${API.replace('/api', '')}${coverArtUrl}` : coverArtUrl))} 
                alt="Cover art" 
                className="w-24 h-24 object-cover rounded-lg border border-border"
                onError={(e) => {
                  console.error('[TrackUploadForm] Cover art preview failed:', coverArtUrl);
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23374151" width="100" height="100"/%3E%3Ctext x="50" y="55" fill="%239CA3AF" font-size="10" text-anchor="middle"%3E?%3C/text%3E%3C/svg%3E';
                }}
              />
              <button
                type="button"
                onClick={() => setCoverArtUrl('')}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors">
              {coverArtUploading ? (
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <ImageIcon className="w-6 h-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                </>
              )}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleCoverArtUpload}
                className="hidden"
                disabled={coverArtUploading}
                data-testid="cover-art-input"
              />
            </label>
          )}
          <div className="text-xs text-muted-foreground">
            <p>JPG, PNG, or WebP</p>
            <p>Max 10MB • Square recommended</p>
            <p className="text-yellow-500/80 mt-1">⚠️ No explicit/adult content</p>
            {uploadMethod === 'dsp' && (
              <p className="text-purple-400 mt-1">Add your own cover art for DSP tracks!</p>
            )}
          </div>
        </div>
      </div>

      {/* Track Details - Common fields */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm mb-1 block">Song Title *</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter song title"
            data-testid="track-title-input"
          />
        </div>
        <div>
          <Label className="text-sm mb-1 block">Primary Genre * (for charts)</Label>
          <GenreDropdown
            value={genre}
            onChange={setGenre}
            required={true}
            placeholder="Select genre..."
          />
        </div>
      </div>

      {/* Monetize This Content */}
      <MonetizeToggle
        monetizeConfig={monetizeConfig}
        setMonetizeConfig={setMonetizeConfig}
        lockConfig={lockConfig}
        setLockConfig={setLockConfig}
        compact
      />

      {/* Ownership Confirmation */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Checkbox 
            id="ownership-confirm-new"
            checked={ownershipConfirmed}
            onCheckedChange={setOwnershipConfirmed}
            data-testid="ownership-checkbox"
          />
          <label htmlFor="ownership-confirm-new" className="text-xs text-muted-foreground cursor-pointer">
            <strong className="text-orange-400">Legal Confirmation:</strong> I confirm I own or control 
            the rights to this music. False confirmation may result in removal and account action.
          </label>
        </div>
      </div>

      {/* Submit Button - type="button" to prevent form submission issues */}
      <Button 
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        className="w-full touch-manipulation"
        data-testid="submit-track-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Adding Track...
          </>
        ) : (
          <>
            <Music className="w-4 h-4 mr-2" />
            Add {uploadMethod === 'dsp' ? 'DSP' : 'Direct'} Track
          </>
        )}
      </Button>
    </form>
  );
}
